/**
 * Brand-mark icons for the supported VCS providers, as inline SVG (the app has no
 * icon library; this matches the inline-<svg> convention in CopyBlock/CopyOverlay).
 * Both marks use `fill="currentColor"` so they inherit the surrounding text colour
 * (accent when used inside a link, muted otherwise). Unknown provider -> a generic
 * link glyph so the row still reads as "has a repo".
 */

export function RepoProviderIcon({ provider, size = 14 }: {
  provider: string | null
  size?: number
}) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    'aria-hidden': true as const,
    style: { verticalAlign: 'text-bottom' as const, flexShrink: 0 },
  }

  if (provider === 'github') {
    return (
      <svg {...common}>
        <path d="M12 .5C5.37.5 0 5.87 0 12.5c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58 0-.29-.01-1.04-.02-2.05-3.34.73-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.74.08-.74 1.21.09 1.84 1.24 1.84 1.24 1.07 1.84 2.81 1.31 3.5 1 .11-.78.42-1.31.76-1.61-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.12-.3-.54-1.52.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6.01 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.66.24 2.88.12 3.18.77.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.49 5.92.43.37.81 1.1.81 2.22 0 1.6-.01 2.9-.01 3.29 0 .32.21.7.82.58A12.01 12.01 0 0 0 24 12.5C24 5.87 18.63.5 12 .5z" />
      </svg>
    )
  }

  if (provider === 'gitlab') {
    return (
      <svg {...common}>
        <path d="M12 23.05 16.42 9.5H7.58L12 23.05Z" />
        <path d="M12 23.05 7.58 9.5H1.39L12 23.05Z" />
        <path d="M1.39 9.5.05 13.6a.92.92 0 0 0 .33 1.03L12 23.05 1.39 9.5Z" />
        <path d="M1.39 9.5h6.19L4.92 1.32a.46.46 0 0 0-.88 0L1.39 9.5Z" />
        <path d="M12 23.05 16.42 9.5h6.19L12 23.05Z" />
        <path d="M22.61 9.5l1.34 4.1a.92.92 0 0 1-.33 1.03L12 23.05 22.61 9.5Z" />
        <path d="M22.61 9.5h-6.19l2.66-8.18a.46.46 0 0 1 .88 0L22.61 9.5Z" />
      </svg>
    )
  }

  // Generic link glyph for any other / unknown provider.
  return (
    <svg {...common} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

/**
 * Upstream-registry brand marks (Terraform / OpenTofu) for the provider tables, keyed on the
 * `upstream` column value. Single-colour `currentColor` paths like the VCS marks above so they
 * dim/tint with the surrounding text. Unknown upstream -> nothing (the cell reads fine bare).
 */
export function RegistryIcon({ upstream, size = 14 }: {
  upstream: string | null | undefined
  size?: number
}) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    'aria-hidden': true as const,
    style: { verticalAlign: 'text-bottom' as const, flexShrink: 0 },
  }

  if (upstream === 'terraform.io') {
    // Terraform "T" block mark: left block, two stacked right blocks, far-right block.
    return (
      <svg {...common}>
        <path d="M1 2l6.5 3.75v6L1 8V2Z" />
        <path d="M8.5 6.5l6.5 3.75v6L8.5 12.5v-6Z" />
        <path d="M22.5 6.5L16 10.25v6l6.5-3.75v-6Z" />
        <path d="M8.5 13.5l6.5 3.75v6L8.5 19.5v-6Z" />
      </svg>
    )
  }

  if (upstream === 'opentofu.org') {
    // OpenTofu isometric cube: hexagon outline with the three visible faces.
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2.5 20.5 7.25v9.5L12 21.5l-8.5-4.75v-9.5L12 2.5Z" />
        <path d="M3.5 7.25 12 12l8.5-4.75M12 12v9.5" />
      </svg>
    )
  }

  return null
}

/**
 * Non-VCS actor glyphs for the merged "Pushed by" column - a person (human), a
 * chip/robot (service account / bot token), and a question mark (legacy rows with
 * no captured actor). Same inline-<svg>, `currentColor` convention as the provider
 * marks above so all rows in the column share one icon family.
 */
export function ActorIcon({ kind, size = 14 }: {
  kind: 'human' | 'bot' | 'unknown'
  size?: number
}) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'aria-hidden': true as const,
    style: { verticalAlign: 'text-bottom' as const, flexShrink: 0 },
  }
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

  if (kind === 'human') {
    return (
      <svg {...common} {...stroke}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </svg>
    )
  }

  if (kind === 'bot') {
    return (
      <svg {...common} {...stroke}>
        <rect x="4" y="8" width="16" height="12" rx="2" />
        <path d="M12 8V4" />
        <circle cx="12" cy="3" r="1" fill="currentColor" stroke="none" />
        <path d="M9 13h.01M15 13h.01" />
      </svg>
    )
  }

  // unknown / legacy
  return (
    <svg {...common} {...stroke}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3" />
      <path d="M12 17h.01" />
    </svg>
  )
}
